import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Body } from '~/components/atom/Typography';
import {
  OutputFieldInvisible,
  WizardStepProps,
} from '~/components/organism/Wizard/context/WizardContext';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import useWizardStep from '~/hooks/useWizardStep';
import useGetAppWaardeCheckQuery from '../hooks/useGetAppWaardeCheckQuery';
import useUpdateAppWaardecheckMutation from '../hooks/useUpdateAppWaardeCheckMutation';
import transformToInput from '~/components/page/Apps/components/WaardecheckTemplate/utils/transformToInput';
import { AppWaardecheckFieldsFragment } from '~/components/page/Apps/components/WaardecheckTemplate';
import ToggleCheckbox from '~/components/molecule/ToggleCheckbox';
import JustificationContainer from '~/components/atom/JustificationContainer';
import BndlLoading from '~/components/atom/BndlLoading';
import { OutputMap } from '../../..';
import TEST_ID from './index.testid';

export const id = 'WaardecheckVerification';
export const title = 'Telefoonnummervalidatie';

export type OutputType = {
  type: typeof id;
  verificationEnabled: OutputFieldInvisible<boolean>;
};

const text = {
  description:
    'Schakel telefoonnummervalidatie in om het telefoonnummer van bezoekers te verifiëren via SMS voordat zij de waardecheck ontvangen. Dit zorgt voor kwalitatievere leads.',
  cost: 'Kosten: €0,39 per validatie (eenmalig per contact). Na validatie kan de bezoeker de waardecheck onbeperkt gebruiken.',
  toggleLabel: 'Telefoonnummervalidatie inschakelen',
  error: 'Er is iets misgegaan tijdens het opslaan, probeer het later opnieuw',
};

export const Component: React.FCC<WizardStepProps> = ({ step, outputMap }) => {
  const { id: accountId } = useCurrentAccount();
  const currentOutput = outputMap[id] as OutputType;
  const useQueryHook = useGetAppWaardeCheckQuery(step.metadata?.typename);
  const useUpdateMutation = useUpdateAppWaardecheckMutation(
    step.metadata?.typename,
  );
  const [updateMutation] = useUpdateMutation();
  const [value, setValue] = useState<boolean>(
    currentOutput.verificationEnabled.value,
  );

  const { data: queryData, loading } = useQueryHook({
    variables: { accountId },
  });

  const data = (() => {
    if (queryData) {
      if ('getAppVBOWaardecheck' in queryData) {
        return queryData.getAppVBOWaardecheck;
      }
      if ('getAppBBWaardecheck' in queryData) {
        return queryData.getAppBBWaardecheck;
      }
    }
    return undefined;
  })();

  const onBeforeNext = useCallback(
    async (outputMap: OutputMap) => {
      const currentOutput = outputMap[id] as OutputType;
      const verificationEnabled = currentOutput.verificationEnabled.value;

      if (!data) return currentOutput;

      // @ts-ignore Generic typing between BB and VBO
      const fields: AppWaardecheckFieldsFragment = {
        ...data,
        general: {
          ...data.general,
          verificationEnabled,
        },
      };

      const update = transformToInput(fields);

      try {
        await updateMutation({
          variables: {
            accountId,
            update,
          },
        });
      } catch {
        throw new Error(text.error);
      }

      return currentOutput;
    },
    [accountId, data, updateMutation],
  );

  const stepOptions = useMemo(() => ({ onBeforeNext }), [onBeforeNext]);
  const [, api] = useWizardStep(step.id, stepOptions);

  useEffect(() => {
    api.free({
      type: id,
      verificationEnabled: { type: 'invisible', value },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (loading) return <BndlLoading />;

  return (
    <JustificationContainer
      direction="column"
      margin={[null, null, 'l', null]}
      dataTestId={TEST_ID.CONTAINER}
    >
      <Body margin={[null, null, 'm', null]}>{text.description}</Body>
      <Body margin={[null, null, 'm', null]}>{text.cost}</Body>
      <ToggleCheckbox
        label={text.toggleLabel}
        value={value}
        onChange={() => setValue(prev => !prev)}
        dataTestId={TEST_ID.TOGGLE}
      />
    </JustificationContainer>
  );
};

export default {
  id,
  title,
};
